import { groupBy } from 'lodash-es';
import { Area, IViolation, TaskListCharacteristic } from 'src/app/models';

interface ICompareSequence {
  code: number;
  characteristic: TaskListCharacteristic;
}

export abstract class TechnicalSpecificationSequenceValidation {
  public static validate(characteristics: TaskListCharacteristic[]): IViolation[] {
    return TechnicalSpecificationSequenceValidation.checkSequence(characteristics).reduce(
      (results: IViolation[], characteristic) => {
        if (!characteristic) {
          return results;
        }

        return [
          ...results,
          {
            message: `Some codes are not in the right sequence order`,
            pathToViolation: [
              { area: Area.Specifications },
              {
                area: Area.TechnicalSpecification,
                displayId: `${characteristic.taskListGroup}-${characteristic.groupCounter}`,
                id: `Q-${characteristic.taskListGroup}-${characteristic.groupCounter}`,
              },
              {
                area: Area.TechnicalSpecificationOperation,
                id: characteristic.infoField2,
              },
              {
                area: Area.TechnicalSpecificationCharacteristics,
                displayId: characteristic.characteristic,
                id: characteristic.id,
              },
            ],
          },
        ];
      },
      []
    );
  }

  private static checkSequence(characteristics: TaskListCharacteristic[]) {
    const groupedCharacteristics = groupBy(
      characteristics,
      (characteristic) => characteristic.infoField2
    );

    const exclude = ['PRODUCT SAFETY', 'Item Description'];

    return Object.keys(groupedCharacteristics)
      .filter((key) => exclude.some((value) => value !== key))
      .map((key) => {
        const sequence = groupedCharacteristics[key].reduce(
          (result: ICompareSequence[], characteristic) => {
            const identifier =
              TechnicalSpecificationSequenceValidation.getSequenceIdentifier(characteristic);

            if (identifier) {
              result.push({ code: Number(identifier), characteristic });
            }

            return result;
          },
          []
        );

        const sorted = sequence.slice().sort((a, b) => a.code - b.code);

        const failed = sorted.find((sortedCode, i) => sequence[i].code !== sortedCode.code);

        return failed ? failed.characteristic : undefined;
      });
  }

  private static getSequenceIdentifier(characteristic: TaskListCharacteristic): string | undefined {
    /**
     * Example 1: W3A: +/-1% Weight dev. calculated in %
     * Example 2: M4_W:Knob - Centre distance Width
     *
     * Group 1: 3 - 4
     * Group : 'something'
     *
     * use group 1 to verify sequence.
     */

    const match = /^[\D]+(?<sequence>\d+)[_]?[A-Z]?[\s:]?/u.exec(
      characteristic.shortTextInspectionCharacteristic
    );

    return match?.groups?.sequence;
  }
}
